import { useEffect, useState } from 'react';
import { Award, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  useProfile,
  useUpdateAggregatedReferences,
} from '../hooks/use-dossier-company';
import { CpvPicker } from './cpv-picker';

/**
 * Sprint 5b.1 v2 — Phase C.4c : section Références agrégées.
 *
 * 4 inputs scalaires (count, total, secteurs CPV, note libre).
 * Saisie manuelle, pas de matérialisation depuis l'historique AO.
 */
export function AggregatedReferencesSection() {
  const { data } = useProfile();
  const profile = data?.profile ?? null;
  const update = useUpdateAggregatedReferences();

  const [aoWonCount, setAoWonCount] = useState('');
  const [aoWonTotalAmountEur, setAoWonTotalAmountEur] = useState('');
  const [aoWonSectorsCpv, setAoWonSectorsCpv] = useState<string[]>([]);
  const [referencesNote, setReferencesNote] = useState('');

  useEffect(() => {
    if (profile) {
      setAoWonCount(profile.aoWonCount?.toString() ?? '');
      setAoWonTotalAmountEur(profile.aoWonTotalAmountEur ?? '');
      setAoWonSectorsCpv(profile.aoWonSectorsCpv ?? []);
      setReferencesNote(profile.referencesNote ?? '');
    }
  }, [profile]);

  async function handleSave() {
    const count = aoWonCount ? parseInt(aoWonCount, 10) : null;
    const total = aoWonTotalAmountEur
      ? parseFloat(aoWonTotalAmountEur.replace(/\s/g, '').replace(',', '.'))
      : null;
    await update.mutateAsync({
      aoWonCount: Number.isFinite(count) ? count : null,
      aoWonTotalAmountEur: Number.isFinite(total) ? total : null,
      aoWonSectorsCpv,
      referencesNote: referencesNote || null,
    });
  }

  return (
    <Card className="p-6 space-y-4">
      <div>
        <h3 className="text-base font-semibold flex items-center gap-2">
          <Award className="h-4 w-4" />
          Références agrégées
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          Vue d'ensemble de votre activité AO. Saisie manuelle, alimente les variables{' '}
          <code className="text-xs">{'{{nous.*}}'}</code> et le scoring Sprint 7.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="ao-count">Nombre d'AO remportés</Label>
          <Input
            id="ao-count"
            type="number"
            min={0}
            value={aoWonCount}
            onChange={(e) => setAoWonCount(e.target.value)}
            placeholder="ex. 12"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ao-total">Montant cumulé (€)</Label>
          <Input
            id="ao-total"
            type="text"
            inputMode="decimal"
            value={aoWonTotalAmountEur}
            onChange={(e) => setAoWonTotalAmountEur(e.target.value)}
            placeholder="ex. 2 500 000"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Secteurs CPV des AO remportés</Label>
        <CpvPicker
          value={aoWonSectorsCpv}
          onChange={setAoWonSectorsCpv}
          placeholder="Ajouter un CPV de référence..."
          emptyTagsMessage="Aucun secteur référencé."
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="ref-note">Note de références (texte libre)</Label>
        <Textarea
          id="ref-note"
          value={referencesNote}
          onChange={(e) => setReferencesNote(e.target.value)}
          placeholder="ex. 12 AO remportés depuis 2020, dont des marchés DGFiP et CNAF..."
          maxLength={5000}
          rows={4}
        />
        <p className="text-xs text-muted-foreground">
          {referencesNote.length}/5000 caractères
        </p>
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={update.isPending}>
          {update.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Enregistrer les références
        </Button>
      </div>
    </Card>
  );
}
